import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ViewModeToggle,
  SortSelect,
  useViewMode,
  useSortKey,
  sortItems,
} from "../components/ui/view-helpers";
import { Plus, Pencil, Trash2, ShieldCheck, Search, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  "Allenatore",
  "Vice Allenatore",
  "Preparatore atletico",
  "Preparatore portieri",
  "Dirigente Accompagnatore",
  "Medico",
  "Massaggiatore",
];

const ROLE_COLORS = {
  Allenatore: "bg-sky-100 text-sky-700 border-sky-200",
  "Vice Allenatore": "bg-sky-50 text-sky-700 border-sky-200",
  "Preparatore atletico": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Preparatore portieri": "bg-amber-100 text-amber-700 border-amber-200",
  "Dirigente Accompagnatore": "bg-violet-100 text-violet-700 border-violet-200",
  Medico: "bg-red-100 text-red-700 border-red-200",
  Massaggiatore: "bg-pink-100 text-pink-700 border-pink-200",
};

const SORT_OPTIONS = [
  { value: "name", label: "Alfabetico" },
  { value: "role", label: "Ruolo" },
];

function nameParts(s) {
  // staff has just "name" — split on first space for last/first heuristic
  const parts = (s.name || "").trim().split(/\s+/);
  if (parts.length >= 2) {
    return { last_name: parts.slice(1).join(" "), first_name: parts[0] };
  }
  return { last_name: s.name || "", first_name: "" };
}

const empty = {
  name: "",
  role: "Allenatore",
  figc_card_number: "",
  fiscal_code: "",
  phone: "",
  email: "",
};

export default function StaffPage() {
  const { activeTeam, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, changeView] = useViewMode("staffViewMode");
  const [sortKey, setSortKey] = useSortKey("staffSortKey", "name");

  const accessors = {
    last_name: (it) => nameParts(it).last_name,
    first_name: (it) => nameParts(it).first_name,
    role: (it) => it.role,
  };

  const filtered = useMemo(() => {
    const matched = items.filter((s) =>
      `${s.name} ${s.role || ""} ${s.email || ""} ${s.phone || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    return sortItems(matched, sortKey, accessors);
  }, [items, search, sortKey, accessors]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const s of filtered) {
      const r = ROLES.includes(s.role) ? s.role : "Altro";
      (buckets[r] = buckets[r] || []).push(s);
    }
    return [...ROLES, "Altro"]
      .filter((r) => buckets[r]?.length)
      .map((role) => ({ role, items: buckets[role] }));
  }, [filtered]);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/staff`);
    setItems(data);
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Nome richiesto");
      return;
    }
    const payload = { ...form, team_id: activeTeam.id };
    if (editingId) await api.put(`/staff/${editingId}`, payload);
    else await api.post(`/staff`, payload);
    toast.success("Staff salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleEdit = (s) => {
    setForm({ ...empty, ...s });
    setEditingId(s.id);
    setOpen(true);
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare ${s.name}?`)) return;
    await api.delete(`/staff/${s.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Staff tecnico</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Mister, Dirigenti & Sanitari
          </h1>
          <p className="text-slate-500">
            {items.length} membri staff · le matricole FIGC appaiono in distinta gara
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <ViewModeToggle
            value={viewMode}
            onChange={changeView}
            groupLabel="Per ruolo"
            testid="staff-view-toggle"
          />
          <SortSelect
            value={sortKey}
            onChange={setSortKey}
            options={SORT_OPTIONS}
            testid="staff-sort"
          />
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-56"
              data-testid="staff-search"
            />
          </div>
          {canEdit && (
            <Button
              onClick={() => {
                setForm(empty);
                setEditingId(null);
                setOpen(true);
              }}
              className="rounded-full bg-sky-500 hover:bg-sky-600"
              data-testid="new-staff-btn"
            >
              <Plus size={16} className="mr-1" /> Nuovo
            </Button>
          )}
        </div>
      </div>

      {viewMode === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <StaffCardItem key={s.id} s={s} canEdit={canEdit} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
          {filtered.length === 0 && (
            <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
              Nessun membro. Aggiungi mister e dirigenti per averli automaticamente nella distinta gara.
            </Card>
          )}
        </div>
      )}

      {viewMode === "list" && (
        <Card className="overflow-hidden" data-testid="staff-list-view">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Nessun membro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Nome</th>
                    <th className="px-4 py-3 text-left">Ruolo</th>
                    <th className="px-4 py-3 text-left">Tessera FIGC</th>
                    <th className="px-4 py-3 text-left">Contatti</th>
                    {canEdit && <th className="px-4 py-3 text-right">Azioni</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`staff-row-${s.id}`}>
                      <td className="px-4 py-3 font-medium">{s.name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`${ROLE_COLORS[s.role] || ""} font-medium`}>
                          {s.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                        {s.figc_card_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 space-x-2">
                        {s.phone && <span>📞 {s.phone}</span>}
                        {s.email && <span>✉ {s.email}</span>}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(s)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-red-600 hover:bg-red-50">
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {viewMode === "group" && (
        <div className="space-y-6" data-testid="staff-group-view">
          {grouped.length === 0 && (
            <Card className="p-10 text-center text-slate-500 border-dashed">Nessun membro.</Card>
          )}
          {grouped.map(({ role, items: list }) => (
            <div key={role}>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className={`text-sm font-bold ${ROLE_COLORS[role] || ""}`}>
                  {role}
                </Badge>
                <span className="text-xs text-slate-400">
                  {list.length} {list.length === 1 ? "membro" : "membri"}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((s) => (
                  <StaffCardItem key={s.id} s={s} canEdit={canEdit} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica" : "Nuovo membro staff"}</DialogTitle>
          <DialogDescription className="sr-only">Inserisci o aggiorna i dati di un membro dello staff tecnico.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome e cognome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tessera FIGC</Label>
                <Input
                  value={form.figc_card_number}
                  onChange={(e) => setForm({ ...form, figc_card_number: e.target.value })}
                  data-testid="staff-figc-input"
                />
              </div>
              <div>
                <Label>Codice fiscale</Label>
                <Input
                  value={form.fiscal_code}
                  onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
                  maxLength={16}
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-staff-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffCardItem({ s, canEdit, onEdit, onDelete }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={`staff-${s.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <ShieldCheck size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold">{s.name}</div>
          <Badge variant="outline" className={`mt-1 text-[10px] ${ROLE_COLORS[s.role] || ""} font-medium`}>
            {s.role}
          </Badge>
          {s.figc_card_number && (
            <div className="text-xs text-slate-600 mt-2 font-mono">
              Tessera FIGC: {s.figc_card_number}
            </div>
          )}
          {s.phone && (
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Phone size={11} /> {s.phone}
            </div>
          )}
          {s.email && (
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
              <Mail size={11} /> {s.email}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-0.5">
            <Button size="icon" variant="ghost" onClick={() => onEdit(s)}>
              <Pencil size={14} />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(s)}>
              <Trash2 size={14} className="text-red-500" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}